import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import {
    Button, Card, Alert, Spinner,
} from './shared';

const fmtDate = (d) => d ? new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtCost = (c) => `$${Number(c).toFixed(4)}`;

export default function AdminBudgetUsage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterUserId, setFilterUserId] = useState('');
    const [filterUsageType, setFilterUsageType] = useState('');
    const [page, setPage] = useState(0);

    useEffect(() => { loadItems(); }, [filterUserId, filterUsageType, page]);

    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await apiClient.listBudgetUsage(
                filterUserId || undefined,
                undefined,
                true,
                page, 50,
                filterUsageType || undefined
            );
            setItems(res.items || []);
            setTotal(res.total || 0);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    const handleResetUser = async (userId) => {
        if (!confirm(`Reset tất cả usage records của user ${userId}? Cache sẽ được clear.`)) return;
        try {
            const res = await apiClient.resetUserUsage(userId);
            setSuccessMessage(res.message);
            loadItems();
        } catch (err) {
            setError(err.message || 'Reset failed');
        }
    };

    const handleResetAll = async () => {
        if (!confirm('Reset TẤT CẢ usage records của TẤT CẢ users? Thao tác này không thể hoàn tác.')) return;
        try {
            const res = await apiClient.resetAllUsage();
            setSuccessMessage(res.message);
            loadItems();
        } catch (err) {
            setError(err.message || 'Reset failed');
        }
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    const fmtCost = (c) => `$${Number(c).toFixed(4)}`;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Budget Usage Records</h2>
                <Button variant="danger" size="sm" onClick={handleResetAll}>Reset All</Button>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert type="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">User ID</label>
                    <input
                        type="text"
                        value={filterUserId}
                        onChange={(e) => { setFilterUserId(e.target.value); setPage(0); }}
                        placeholder="Filter by user ID"
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48"
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-500 mb-1">Usage Type</label>
                    <select
                        value={filterUsageType}
                        onChange={(e) => { setFilterUsageType(e.target.value); setPage(0); }}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                    >
                        <option value="">All</option>
                        <option value="regular">Regular</option>
                        <option value="trial">Trial</option>
                    </select>
                </div>
                <span className="text-xs text-gray-400 pb-1">{total} records</span>
            </div>

            {loading ? <Spinner /> : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-left text-gray-500">
                                    <th className="px-4 py-2">User</th>
                                    <th className="px-4 py-2">Type</th>
                                    <th className="px-4 py-2">Cost</th>
                                    <th className="px-4 py-2">Requests</th>
                                    <th className="px-4 py-2">Tokens</th>
                                    <th className="px-4 py-2">Bucket Start</th>
                                    <th className="px-4 py-2">Bucket End</th>
                                    <th className="px-4 py-2">Updated</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="px-4 py-2 font-mono text-xs">{item.user_id}</td>
                                        <td className="px-4 py-2">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${item.usage_type === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {item.usage_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 font-medium">{fmtCost(item.cost)}</td>
                                        <td className="px-4 py-2 text-gray-600">{item.request_count}</td>
                                        <td className="px-4 py-2 text-gray-600">{item.token_count.toLocaleString()}</td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(item.bucket_start)}</td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(item.bucket_end)}</td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(item.updated_at)}</td>
                                        <td className="px-4 py-2">
                                            <button
                                                onClick={() => handleResetUser(item.user_id)}
                                                className="text-red-500 hover:text-red-700 text-xs"
                                            >
                                                Reset
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No usage records</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {total > 50 && (
                        <div className="flex justify-center gap-2 p-3 border-t border-gray-100">
                            <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
                            <span className="text-sm text-gray-500 py-1">Page {page + 1}</span>
                            <Button variant="secondary" size="sm" disabled={items.length < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}
